/**
 * Theme API Client Service
 * 
 * Provides functions to interact with theme and product management APIs.
 */

import apiService from './api';
import {
  ColorTheme,
  CreateThemeRequest,
  UpdateThemeRequest,
  ThemesResponse,
  DataProduct,
  CreateProductRequest,
  UpdateProductRequest,
  AddAttributeRequest,
  ProductsResponse,
} from '../types/themes';
import themeCache from './themeCache';

// Admin Theme Management (System Themes)

export async function listAdminThemes(
  dataProduct?: string,
  attribute?: string
): Promise<ColorTheme[]> {
  try {
    const params = new URLSearchParams();
    if (dataProduct) params.append('data_product', dataProduct);
    if (attribute) params.append('attribute', attribute);

    const queryString = params.toString();
    const url = `/admin/themes${queryString ? `?${queryString}` : ''}`;
    
    const response = await apiService.get<ThemesResponse>(url);
    if (!response.data) {
      console.warn(`listAdminThemes: API returned status ${response.statusCode} with no data`);
      return [];
    }
    const data = response.data as ThemesResponse;
    
    if (data.status === 'success' && data.themes) {
      // Cache the themes
      data.themes.forEach(theme => themeCache.setTheme(theme));
      return data.themes;
    }
    
    throw new Error(data.error || 'Failed to list themes');
  } catch (error) {
    console.error('Error listing admin themes:', error);
    throw error;
  }
}

export async function getAdminTheme(themeId: string): Promise<ColorTheme> {
  try {
    const response = await apiService.get<ThemesResponse>(`/admin/themes/${themeId}`);
    if (!response.data) throw new Error(`API returned ${response.statusCode}`);
    const data = response.data as ThemesResponse;
    
    if (data.status === 'success' && data.theme) {
      themeCache.setTheme(data.theme);
      return data.theme;
    }
    
    throw new Error(data.error || 'Failed to get theme');
  } catch (error) {
    console.error('Error getting admin theme:', error);
    throw error;
  }
}

export async function createAdminTheme(request: CreateThemeRequest): Promise<ColorTheme> {
  try {
    const response = await apiService.post<ThemesResponse>('/admin/themes', request);
    if (!response.data) throw new Error(`API returned ${response.statusCode}`);
    const data = response.data as ThemesResponse;
    
    if (data.status === 'success' && data.theme) {
      themeCache.setTheme(data.theme);
      // Invalidate cache for this product/attribute to refresh lists
      themeCache.invalidate(data.theme.data_product, data.theme.attribute);
      return data.theme;
    }
    
    throw new Error(data.error || 'Failed to create theme');
  } catch (error) {
    console.error('Error creating admin theme:', error);
    throw error;
  }
}

export async function updateAdminTheme(
  themeId: string,
  request: UpdateThemeRequest
): Promise<ColorTheme> {
  try {
    const response = await apiService.put<ThemesResponse>(`/admin/themes/${themeId}`, request);
    if (!response.data) throw new Error(`API returned ${response.statusCode}`);
    const data = response.data as ThemesResponse;
    
    if (data.status === 'success' && data.theme) {
      themeCache.setTheme(data.theme);
      // Invalidate cache for this product/attribute to refresh lists
      themeCache.invalidate(data.theme.data_product, data.theme.attribute);
      return data.theme;
    }
    
    throw new Error(data.error || 'Failed to update theme');
  } catch (error) {
    console.error('Error updating admin theme:', error);
    throw error;
  }
}

export async function deleteAdminTheme(themeId: string): Promise<void> {
  try {
    const response = await apiService.delete<ThemesResponse>(`/admin/themes/${themeId}`);
    if (!response.data) throw new Error(`API returned ${response.statusCode}`);
    const data = response.data as ThemesResponse;
    
    if (data.status === 'success') {
      // Invalidate entire cache since we don't know which product/attribute
      themeCache.invalidate();
      return;
    }
    
    throw new Error(data.error || 'Failed to delete theme');
  } catch (error) {
    console.error('Error deleting admin theme:', error);
    throw error;
  }
}

export async function setDefaultTheme(themeId: string): Promise<ColorTheme> {
  try {
    const response = await apiService.post<ThemesResponse>(`/admin/themes/${themeId}/set-default`, {});
    if (!response.data) throw new Error(`API returned ${response.statusCode}`);
    const data = response.data as ThemesResponse;
    
    if (data.status === 'success' && data.theme) {
      themeCache.setTheme(data.theme);
      // Invalidate cache for this product/attribute to refresh lists
      themeCache.invalidate(data.theme.data_product, data.theme.attribute);
      return data.theme;
    }
    
    throw new Error(data.error || 'Failed to set default theme');
  } catch (error) {
    console.error('Error setting default theme:', error);
    throw error;
  }
}

// User Theme Management (Custom Themes)

export async function listUserThemes(
  dataProduct: string,
  attribute: string
): Promise<ColorTheme[]> {
  try {
    const params = new URLSearchParams();
    params.append('data_product', dataProduct);
    params.append('attribute', attribute);

    const response = await apiService.get<ThemesResponse>(`/api/themes?${params.toString()}`);
    
    if (!response.data) {
      console.warn(`listUserThemes: API returned status ${response.statusCode} with no data`);
      return [];
    }
    
    const data = response.data as ThemesResponse;
    
    if (data.status === 'success' && data.themes) {
      // Cache the themes
      data.themes.forEach(theme => themeCache.setTheme(theme));
      return data.themes;
    }
    
    throw new Error(data.error || 'Failed to list themes');
  } catch (error) {
    console.error('Error listing user themes:', error);
    throw error;
  }
}

export async function getDefaultTheme(
  dataProduct: string,
  attribute: string
): Promise<ColorTheme> {
  try {
    // Check cache first
    const cached = themeCache.getTheme(dataProduct, attribute);
    if (cached) {
      return cached;
    }

    const params = new URLSearchParams();
    params.append('data_product', dataProduct);
    params.append('attribute', attribute);

    const response = await apiService.get<ThemesResponse>(`/api/themes/default?${params.toString()}`);
    if (!response.data) throw new Error(`API returned ${response.statusCode}`);
    const data = response.data as ThemesResponse;
    
    if (data.status === 'success' && data.theme) {
      themeCache.setTheme(data.theme);
      return data.theme;
    }
    
    throw new Error(data.error || 'Failed to get default theme');
  } catch (error) {
    console.error('Error getting default theme:', error);
    throw error;
  }
}

export async function createUserTheme(request: CreateThemeRequest): Promise<ColorTheme> {
  try {
    const response = await apiService.post<ThemesResponse>('/api/themes', request);
    if (!response.data) throw new Error(`API returned ${response.statusCode}`);
    const data = response.data as ThemesResponse;
    
    if (data.status === 'success' && data.theme) {
      themeCache.setTheme(data.theme);
      return data.theme;
    }
    
    throw new Error(data.error || 'Failed to create theme');
  } catch (error) {
    console.error('Error creating user theme:', error);
    throw error;
  }
}

export async function updateUserTheme(
  themeId: string,
  request: UpdateThemeRequest
): Promise<ColorTheme> {
  try {
    const response = await apiService.put<ThemesResponse>(`/api/themes/${themeId}`, request);
    if (!response.data) throw new Error(`API returned ${response.statusCode}`);
    const data = response.data as ThemesResponse;
    
    if (data.status === 'success' && data.theme) {
      themeCache.setTheme(data.theme);
      return data.theme;
    }
    
    throw new Error(data.error || 'Failed to update theme');
  } catch (error) {
    console.error('Error updating user theme:', error);
    throw error;
  }
}

export async function deleteUserTheme(themeId: string): Promise<void> {
  try {
    const response = await apiService.delete<ThemesResponse>(`/api/themes/${themeId}`);
    if (!response.data) throw new Error(`API returned ${response.statusCode}`);
    const data = response.data as ThemesResponse;
    
    if (data.status === 'success') {
      // Invalidate cache
      themeCache.invalidate();
      return;
    }
    
    throw new Error(data.error || 'Failed to delete theme');
  } catch (error) {
    console.error('Error deleting user theme:', error);
    throw error;
  }
}

// Product Management (Admin only)

export async function listProducts(): Promise<DataProduct[]> {
  try {
    const response = await apiService.get<ProductsResponse>('/admin/products');
    if (!response.data) throw new Error(`API returned ${response.statusCode}`);
    const data = response.data as ProductsResponse;
    
    if (data.status === 'success' && data.products) {
      return data.products;
    }
    
    throw new Error(data.error || 'Failed to list products');
  } catch (error) {
    console.error('Error listing products:', error);
    throw error;
  }
}

export async function getProduct(productId: string): Promise<DataProduct> {
  try {
    const response = await apiService.get<ProductsResponse>(`/admin/products/${productId}`);
    if (!response.data) throw new Error(`API returned ${response.statusCode}`);
    const data = response.data as ProductsResponse;
    
    if (data.status === 'success' && data.product) {
      return data.product;
    }
    
    throw new Error(data.error || 'Failed to get product');
  } catch (error) {
    console.error('Error getting product:', error);
    throw error;
  }
}

export async function createProduct(request: CreateProductRequest): Promise<DataProduct> {
  try {
    const response = await apiService.post<ProductsResponse>('/admin/products', request);
    if (!response.data) throw new Error(`API returned ${response.statusCode}`);
    const data = response.data as ProductsResponse;
    
    if (data.status === 'success' && data.product) {
      return data.product;
    }
    
    throw new Error(data.error || 'Failed to create product');
  } catch (error) {
    console.error('Error creating product:', error);
    throw error;
  }
}

export async function updateProduct(
  productId: string,
  request: UpdateProductRequest
): Promise<DataProduct> {
  try {
    const response = await apiService.put<ProductsResponse>(`/admin/products/${productId}`, request);
    if (!response.data) throw new Error(`API returned ${response.statusCode}`);
    const data = response.data as ProductsResponse;
    
    if (data.status === 'success' && data.product) {
      return data.product;
    }
    
    throw new Error(data.error || 'Failed to update product');
  } catch (error) {
    console.error('Error updating product:', error);
    throw error;
  }
}

export async function deleteProduct(productId: string): Promise<void> {
  try {
    const response = await apiService.delete<ProductsResponse>(`/admin/products/${productId}`);
    if (!response.data) throw new Error(`API returned ${response.statusCode}`);
    const data = response.data as ProductsResponse;
    
    if (data.status === 'success') {
      return;
    }
    
    throw new Error(data.error || 'Failed to delete product');
  } catch (error) {
    console.error('Error deleting product:', error);
    throw error;
  }
}

export async function addAttribute(
  productId: string,
  request: AddAttributeRequest
): Promise<DataProduct> {
  try {
    const response = await apiService.post<ProductsResponse>(
      `/admin/products/${productId}/attributes`,
      request
    );
    if (!response.data) throw new Error(`API returned ${response.statusCode}`);
    const data = response.data as ProductsResponse;
    
    if (data.status === 'success' && data.product) {
      return data.product;
    }
    
    throw new Error(data.error || 'Failed to add attribute');
  } catch (error) {
    console.error('Error adding attribute:', error);
    throw error;
  }
}

export async function updateAttribute(
  productId: string,
  attributeId: string,
  request: Partial<AddAttributeRequest>
): Promise<DataProduct> {
  try {
    const response = await apiService.put<ProductsResponse>(
      `/admin/products/${productId}/attributes/${attributeId}`,
      request
    );
    if (!response.data) throw new Error(`API returned ${response.statusCode}`);
    const data = response.data as ProductsResponse;
    
    if (data.status === 'success' && data.product) {
      return data.product;
    }
    
    throw new Error(data.error || 'Failed to update attribute');
  } catch (error) {
    console.error('Error updating attribute:', error);
    throw error;
  }
}

export async function deleteAttribute(
  productId: string,
  attributeId: string
): Promise<void> {
  try {
    const response = await apiService.delete<ProductsResponse>(
      `/admin/products/${productId}/attributes/${attributeId}`
    );
    if (!response.data) throw new Error(`API returned ${response.statusCode}`);
    const data = response.data as ProductsResponse;
    
    if (data.status === 'success') {
      return;
    }
    
    throw new Error(data.error || 'Failed to delete attribute');
  } catch (error) {
    console.error('Error deleting attribute:', error);
    throw error;
  }
}
